import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import Header from "./components/Header/Header";
import LeftSidebar from "./components/LeftSidebar/LeftSidebar";
import RightSidebar from "./components/RightSidebar/RightSidebar";
import MainContent from "./components/MainContent/MainContent";
import AdditionalPanel from "./components/LeftSidebar/AdditionalPanel/AdditionalPanel";
import { useLayerManagement } from "./utils/addingLayers/LayerManagement";
import { useImportLayer } from "./utils/addingLayers/ImportLayer";
import { loadGML, setGMLLayer } from "./loaders/GMLLoader";
import { loadKML, setKMLLayer } from "./loaders/KMLLoader";
import { loadGeoJSON, setGeoJSONLayer } from "./loaders/GeoJsonLoader";
import { loadDXF, setDXFLayers } from "./loaders/DXFLoader";
import { loadShapefile, setShapefileLayer } from "./loaders/ShapefileLoader";
import { loadLAS, setLASLayer } from "./loaders/LASLoader";
import { handleUpdateAttribute } from "./utils/update/UpdateAtribute";
import { handleUpdateGeometry } from "./utils/update/UpdateGeometry";
import { addLayer, removeLayer } from "./utils/addingLayers/AddVoltagesLayers";
import FullscreenOverlay from "./utils/Overlay/FullScreenOverlay";
import { GeoJsonLayer } from "@deck.gl/layers";
import { handlePrint } from "./components/print/Print";
import { useAccessRights } from "./utils/LoginForm/Auth/useAccessRights";
import { ToastContainer } from "react-toastify";
import HeaderMenu from "./components/Header/HeaderMenu";
import LoginForm from "./utils/LoginForm/LoginForm";
import { loginUser } from "./utils/LoginForm/Auth/loginUser";
import { useAuth } from "./utils/LoginForm/Auth/AuthProvider";
import "react-toastify/dist/ReactToastify.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import "./App.css";

function App() {
  const mapRef = useRef(null);
  const deckRef = useRef(null);

  const { initialized, isAuthenticated, applyLogin } = useAuth();

  const [mapStyle, handleMapStyleChange] = useState(
    "mapbox://styles/amgeosolutions/clyingo6o00yr01pnd4cb0vhe"
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(true);
  const [isAdditionalPanelOpen, setIsAdditionalPanelOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [inputUrl, setInputUrl] = useState("");

  const { fetchData } = useImportLayer(inputUrl);

  let onMapClick;

  const [hoverInfo, setHoverInfo] = useState(null);
  const [layerIndex, setLayerIndex] = useState(0);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const [substationOptions, setSubstationOptions] = useState({
    broj_korisnika_ts: { checked: false },
    saidi_ts: { checked: false },
    saifi_ts: { checked: false },
    aktivne_ts: { checked: false },
    gubici_ts: { checked: false },
  });

  const [clickInfo, setClickInfo] = useState(null);
  const [activeMode, setActiveMode] = useState("");
  const [measurementResult, setMeasurementResult] = useState(null);
  const [lasLayers, setLasLayers] = useState([]);
  const [, setSelectedSubstationsLoss] = useState([]);

  const [regionStubovi, setRegionStubovi] = useState([]);
  const [regionTrafostanice, setRegionTrafostanice] = useState([]);
  const [regionVodovi, setRegionVodovi] = useState([]);
  const [layerInfo, setLayerInfo] = useState([]);

  const [popupInfo, setPopupInfo] = useState(null);
  const [currentDrawing, setCurrentDrawing] = useState([]);
  const [currentMeasureDrawing, setCurrentMeasureDrawing] = useState([]);

  const [typeName, setTypeName] = useState("");
  const [layerName, setLayerName] = useState("");
  const [layerEPSG, setLayerEPSG] = useState("");

  const [isUpdated, setIsUpdated] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isCreated, setIsCreated] = useState(false);

  const [overlay, setOverlay] = useState({ active: false, message: "" });
  const [isTrafoActive, setIsTrafoActive] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [checkedLayers, setCheckedLayers] = useState([]);
  const [multiSelectedFeatures, setMultiSelectedFeatures] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [prevLeftSidebarCollapsed, setPrevLeftSidebarCollapsed] = useState(false);
  const [networkType, setNetworkType] = useState(null);

  const {
    accessProfile,
    idTerenskeJedinice,
    ojGroups,
    isAuthenticated: ojIsAuthenticated,
    user,
  } = useAccessRights();

  const [isOhActive, setIsOhActiveExtern] = useState(false);
  const [activeLayerFilters, setActiveLayerFilters] = useState({});

  const userInfo = {
    name: user?.given_name || "",
    surname: user?.family_name || "",
    unit: ojGroups?.[0] || "",
  };

  const ojInfo = useMemo(
    () => ({
      Šifra: idTerenskeJedinice,
      Naziv: ojGroups?.[0] || null,
      isAuthenticated: ojIsAuthenticated,
    }),
    [idTerenskeJedinice, ojGroups, ojIsAuthenticated]
  );

  const [viewState, setViewState] = useState({
    longitude: 17.977,
    latitude: 44.173,
    zoom: 8,
    pitch: 0,
    bearing: 0,
    is3D: false,
  });

  const handleOpenAdditionalPanel = (panelName) => {
    setPrevLeftSidebarCollapsed(isLeftSidebarCollapsed);
    setIsLeftSidebarCollapsed(true);
    setActivePanel(panelName);
    setIsAdditionalPanelOpen(true);
  };

  const handleCloseAdditionalPanel = () => {
    setIsAdditionalPanelOpen(false);
    setActivePanel(null);
    setIsLeftSidebarCollapsed(prevLeftSidebarCollapsed);
  };

  const {
    wfsLayers,
    wmsLayers,
    handleLayerSubmit,
    submitWmsLayer,
    addLayerFromCatalog,
    setWfsLayers,
    setWmsLayers,
  } = useLayerManagement(fetchData, setViewState, onMapClick, setHoverInfo);

  const handleLoadGML = async (file) => {
    const gml = await loadGML(file);
    if (gml === undefined || gml === null) return;

    setGMLLayer(layerIndex, gml, setHoverInfo, setWfsLayers, setViewState);
    setLayerIndex(layerIndex + 1);
  };

  const handleLoadKML = async (file) => {
    const kml = await loadKML(file);
    if (kml === undefined || kml === null) return;

    setKMLLayer(layerIndex, kml, setHoverInfo, setWfsLayers, setViewState);
    setLayerIndex(layerIndex + 1);
  };

  const handleLoadGeoJson = async (file) => {
    const geojson = await loadGeoJSON(file);
    if (geojson === undefined || geojson === null) return;

    setGeoJSONLayer(layerIndex, geojson, setHoverInfo, setWfsLayers, setViewState);
    setLayerIndex(layerIndex + 1);
  };

  const handleLoadDXF = async (file) => {
    const dxfData = await loadDXF(file);
    if (dxfData === undefined || dxfData === null) return;

    setDXFLayers(layerIndex, dxfData, setHoverInfo, setWfsLayers, setViewState);
    setLayerIndex(layerIndex + 1);
  };

  const handleLoadShapefile = async (shpFile) => {
    const shapefileData = await loadShapefile(shpFile);
    if (shapefileData === undefined || shapefileData === null) return;

    setShapefileLayer(
      layerIndex,
      shapefileData,
      setHoverInfo,
      setWfsLayers,
      setViewState
    );

    setLayerIndex(layerIndex + 1);
  };

  const handleLoadLas = async (file, crs) => {
    const lasData = await loadLAS(file, crs);
    if (lasData === undefined || lasData === null) return;

    setLASLayer(layerIndex, lasData, setHoverInfo, setLasLayers, setViewState);
    setLayerIndex(layerIndex + 1);
  };

  const handleDestroyMeasurement = () => {
    setActiveMode(null);
    setCurrentMeasureDrawing([]);
    setMeasurementResult(null);
  };

  const handleSetCurrentDrawing = (feature) => {
    setCurrentDrawing(feature);

    if (feature && feature.id) {
      const extractedLayerName = feature.id.split(".")[0];
      const nt = feature.properties?.tip_mreze;

      setNetworkType(nt);
      setLayerName(extractedLayerName);
      setTypeName(`elektro:${extractedLayerName}`);
    }
  };

  const getTypeNameFromDrawing = (drawing) => {
    if (!drawing || !drawing.id) return "";

    const localLayerName = drawing.id.split(".")[0];

    return `elektro:${localLayerName}`;
  };

  const handleUpdateAttributeWrapper = useCallback(
    async (newValue) => {
      if (currentDrawing && currentDrawing.id) {
        const dynamicTypeName = getTypeNameFromDrawing(currentDrawing);
        const nt = currentDrawing.properties?.tip_mreze;

        setNetworkType(nt);

        await handleUpdateAttribute(
          currentDrawing.id,
          dynamicTypeName,
          newValue,
          setIsUpdated
        );

        setPopupInfo(null);
        setCurrentDrawing([]);
      } else {
        console.error("No feature selected for updating attribute.");
      }
    },
    [currentDrawing]
  );

  const handleUpdateGeometryWrapper = useCallback(
    async (newCoordinates) => {
      if (currentDrawing && currentDrawing.id) {
        const dynamicTypeName = getTypeNameFromDrawing(currentDrawing);
        const nt = currentDrawing.properties?.tip_mreze;

        setNetworkType(nt);

        await handleUpdateGeometry(
          currentDrawing,
          newCoordinates,
          layerEPSG,
          dynamicTypeName,
          currentDrawing.geometry_name,
          setIsUpdated
        );

        setPopupInfo(null);
        setCurrentDrawing([]);
      } else {
        console.error("No feature selected for updating geometry.");
      }
    },
    [currentDrawing, layerEPSG]
  );

  const updateLayer = useCallback(async () => {
    if (!isDeleted && !isUpdated && !isCreated) return;
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      const newLayer = await addLayer(
        layerName,
        setHoverInfo,
        setClickInfo,
        null,
        setWmsLayers,
        networkType,
        viewState,
        setViewState,
        false,
        setOverlay,
        idTerenskeJedinice,
        wfsLayers
      );

      if (newLayer) {
        setWfsLayers((prevLayers) => [
          ...removeLayer(prevLayers, layerName, networkType),
          ...newLayer,
        ]);
      } else {
        console.error("Greška: newLayer je undefined ili null.");
      }
    } catch (error) {
      console.error("Greška prilikom dodavanja sloja:", error);
    } finally {
      setIsDeleted(false);
      setIsUpdated(false);
      setIsProcessing(false);
      setIsCreated(false);
      setCurrentDrawing([]);
    }
  }, [
    isDeleted,
    isUpdated,
    isCreated,
    isProcessing,
    layerName,
    viewState,
    setWfsLayers,
    setWmsLayers,
    setHoverInfo,
    idTerenskeJedinice,
    networkType,
    wfsLayers,
  ]);

  useEffect(() => {
    updateLayer();
  }, [updateLayer]);

  const multipleSelectedFeatureLayer = new GeoJsonLayer({
    id: "selected-features-layer",
    data: {
      type: "FeatureCollection",
      features:
        Array.isArray(multiSelectedFeatures) && multiSelectedFeatures.length > 0
          ? multiSelectedFeatures
          : selectedFeature
            ? Array.isArray(selectedFeature)
              ? selectedFeature
              : [selectedFeature]
            : [],
    },
    pickable: false,
    stroked: true,
    filled: true,
    getFillColor: [0, 0, 255, 100],
    getLineColor: [0, 0, 255, 255],
    lineWidthMinPixels: 2,
    getLineWidth: 2,
    getPointRadius: 6,
    updateTriggers: {
      data: [multiSelectedFeatures, selectedFeature],
    },
  });

  const handleToggleCompare = useCallback(() => {
    setIsCompareMode((prev) => {
      const next = !prev;

      if (!next) {
        setCheckedLayers([]);
      }

      return next;
    });
  }, []);

  const hasAutoOpenedRightSidebar = useRef(false);

  useEffect(() => {
    if (
      !hasAutoOpenedRightSidebar.current &&
      ojInfo?.isAuthenticated &&
      wfsLayers &&
      wfsLayers.length > 0
    ) {
      setIsRightSidebarCollapsed(false);
      hasAutoOpenedRightSidebar.current = true;
    }
  }, [ojInfo, wfsLayers]);

  useEffect(() => {
    const clearDrawingLayers = async () => {
      setOverlay({
        active: true,
        message: "Uklanjanje slojeva iz crtanja...",
      });

      await new Promise((resolve) => {
        setWfsLayers((prev) =>
          prev.filter((layer) => layer?.props?.source !== "drawing")
        );

        setWmsLayers((prev) =>
          prev.filter((layer) => layer?.props?.source !== "drawing")
        );

        setLayerInfo(null);

        setTimeout(resolve, 150);
      });

      setOverlay({ active: false, message: "" });
    };

    if (activePanel !== "Crtanje") {
      clearDrawingLayers();
    }
  }, [activePanel, setWfsLayers, setWmsLayers]);

  const handleRemoveSelectedFeature = useCallback((featureToRemove) => {
    const matchesFeature = (item) => {
      if (!item || !featureToRemove) return false;

      const sameId = item.id === featureToRemove.id;

      const sameProps =
        JSON.stringify(item.properties || {}) ===
        JSON.stringify(featureToRemove.properties || {});

      return sameId && sameProps;
    };

    setSelectedFeature((prev) => {
      if (!prev) return prev;

      const list = Array.isArray(prev) ? prev.flat().filter(Boolean) : [prev];
      const filtered = list.filter((item) => !matchesFeature(item));

      return filtered.length ? filtered : null;
    });

    setMultiSelectedFeatures((prev) => {
      if (!Array.isArray(prev)) return [];

      return prev
        .flat()
        .filter(Boolean)
        .filter((item) => !matchesFeature(item));
    });
  }, []);

  const handleClearSelectedFeatures = useCallback(() => {
    setSelectedFeature(null);
    setMultiSelectedFeatures([]);
  }, []);

  return (
    <div
      className={`app-container ${isLeftSidebarCollapsed ? "left-collapsed" : ""} ${isRightSidebarCollapsed ? "right-collapsed" : ""
        }`}
    >
      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setOverlay={setOverlay}
        ojInfo={ojInfo}
        setWfsLayers={setWfsLayers}
        setWmsLayers={setWmsLayers}
        setLasLayers={setLasLayers}
        setActivePanel={setActivePanel}
        setIsLeftSidebarCollapsed={setIsLeftSidebarCollapsed}
      />

      {initialized && !isAuthenticated && (
        <LoginForm
          isOpen={true}
          onClose={() => { }}
          onLogin={async (username, password) => {
            const success = await loginUser(
              username,
              password,
              () => { },
              () => { },
              () => { },
              () => { }
            );

            if (success) {
              applyLogin();
            }

            return success;
          }}
        />
      )}

      {(!initialized || isAuthenticated) && (
        <>
          <div className="content">
            {isMenuOpen && (
              <HeaderMenu
                setWfsLayers={setWfsLayers}
                setHoverInfo={setHoverInfo}
                setClickInfo={setClickInfo}
                setWmsLayers={setWmsLayers}
                setSubstationOptions={setSubstationOptions}
                substationOptions={substationOptions}
                viewState={viewState}
                setViewState={setViewState}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                setIsTrafoActive={setIsTrafoActive}
                wfsLayers={wfsLayers}
                setOverlay={setOverlay}
                id_terenske_jedinice={idTerenskeJedinice}
                setIsOhActiveExtern={setIsOhActiveExtern}
                setNetworkType={setNetworkType}
              />
            )}

            {!isMenuOpen && (
              <LeftSidebar
                isCollapsed={isLeftSidebarCollapsed}
                onAddClick={handleOpenAdditionalPanel}
                onToggle={() =>
                  setIsLeftSidebarCollapsed(!isLeftSidebarCollapsed)
                }
                accessProfile={accessProfile}
                activePanel={isAdditionalPanelOpen ? activePanel : null}
                setOverlay={setOverlay}
              />
            )}

            {isAdditionalPanelOpen && activePanel && !isMenuOpen && (
              <AdditionalPanel
                wfsLayers={wfsLayers}
                wmsLayers={wmsLayers}
                activePanel={activePanel}
                setInputUrl={setInputUrl}
                addLayerFromCatalog={addLayerFromCatalog}
                handleLayerSubmit={handleLayerSubmit}
                handleWmsLayerSubmit={submitWmsLayer}
                handleLoadGML={handleLoadGML}
                handleLoadKML={handleLoadKML}
                handleLoadGeoJson={handleLoadGeoJson}
                handleLoadDXF={handleLoadDXF}
                handleLoadShapefile={handleLoadShapefile}
                handleLoadLasFile={handleLoadLas}
                onClose={handleCloseAdditionalPanel}
                setWfsLayers={setWfsLayers}
                setHoverInfo={setHoverInfo}
                handleDestroyMeasurement={handleDestroyMeasurement}
                measurementResult={measurementResult}
                handleClickInfo={setClickInfo}
                setWmsLayers={setWmsLayers}
                handleMapStyleChange={handleMapStyleChange}
                lasLayers={lasLayers}
                setLasLayers={setLasLayers}
                viewState={viewState}
                setViewState={setViewState}
                setLayerInfo={setLayerInfo}
                handlePrint={(scale, preparedSelectedFeatures) =>
                  handlePrint({
                    viewState,
                    layers: wmsLayers,
                    selectedFeature: preparedSelectedFeatures,
                    scale,
                    userInfo,
                    mapRef,
                    deckRef,
                  })
                }
                mapRef={mapRef}
                deckRef={deckRef}
                selectedFeature={multiSelectedFeatures}
                setActiveMode={setActiveMode}
                activeMode={activeMode}
                setCheckedLayers={setCheckedLayers}
                handleToggleCompare={handleToggleCompare}
                isCompareMode={isCompareMode}
                setOverlay={setOverlay}
                id_terenske_jedinice={idTerenskeJedinice}
                userInfo={userInfo}
                activeLayerFilters={activeLayerFilters}
                setActiveLayerFilters={setActiveLayerFilters}
              />
            )}

            <MainContent
              isRightSidebarCollapsed={isRightSidebarCollapsed}
              wfsLayers={wfsLayers}
              wmsLayers={wmsLayers}
              setWfsLayers={setWfsLayers}
              hoverInfo={hoverInfo}
              setSelectedFeature={setSelectedFeature}
              activeMode={activeMode}
              setActiveMode={setActiveMode}
              setMeasurementResult={setMeasurementResult}
              measurementResult={measurementResult}
              clickInfo={clickInfo}
              setClickInfo={setClickInfo}
              substationOptions={substationOptions}
              setRegionStubovi={setRegionStubovi}
              setRegionTrafostanice={setRegionTrafostanice}
              setRegionVodovi={setRegionVodovi}
              mapStyle={mapStyle}
              viewState={viewState}
              setViewState={setViewState}
              lasLayers={lasLayers}
              layerInfo={layerInfo}
              currentDrawing={currentDrawing}
              currentMeasureDrawing={currentMeasureDrawing}
              setCurrentDrawing={setCurrentDrawing}
              setCurrentMeasureDrawing={setCurrentMeasureDrawing}
              setPopupInfo={setPopupInfo}
              setLayerEPSG={setLayerEPSG}
              setIsCreated={setIsCreated}
              setLayerName={setLayerName}
              setMultiSelectedFeatures={setMultiSelectedFeatures}
              multipleSelectedFeatureLayer={multipleSelectedFeatureLayer}
              isCompareMode={isCompareMode}
              checkedLayers={checkedLayers}
              isUpdated={isUpdated}
              isDeleted={isDeleted}
              isMenuOpen={isMenuOpen}
              isLeftSidebarCollapsed={isLeftSidebarCollapsed}
              isTrafoActive={isTrafoActive}
              isExpanded={isExpanded}
              ojInfo={ojInfo}
              setHoverInfo={setHoverInfo}
              handleUpdateGeometryWrapper={handleUpdateGeometryWrapper}
              mapRef={mapRef}
              deckRef={deckRef}
              handleClearSelectedFeatures={handleClearSelectedFeatures}
            />

            <RightSidebar
              isCollapsed={isRightSidebarCollapsed}
              toggleCollapse={() =>
                setIsRightSidebarCollapsed((prev) => !prev)
              }
              selectedFeature={selectedFeature}
              onRemoveSelectedFeature={handleRemoveSelectedFeature}
              substationOptions={substationOptions}
              wfsLayers={wfsLayers}
              setWfsLayers={setWfsLayers}
              clickInfo={clickInfo}
              handleSubstationsLossInformation={setSelectedSubstationsLoss}
              regionStubovi={regionStubovi}
              regionTrafostanice={regionTrafostanice}
              regionVodovi={regionVodovi}
              activeMode={activeMode}
              setActiveMode={setActiveMode}
              handleUpdateAttributeWrapper={handleUpdateAttributeWrapper}
              handleUpdateGeometryWrapper={handleUpdateGeometryWrapper}
              setPopupInfo={setPopupInfo}
              setCurrentDrawing={handleSetCurrentDrawing}
              popupInfo={popupInfo}
              currentDrawing={currentDrawing}
              typeName={typeName}
              setIsDeleted={setIsDeleted}
              setOverlay={setOverlay}
              isOhActive={isOhActive}
              networkType={networkType}
              viewState={viewState}
              setViewState={setViewState}
            />
          </div>

          <FullscreenOverlay active={overlay.active} message={overlay.message} />
          <ToastContainer position="bottom-right" autoClose={3000} />
        </>
      )}
    </div>
  );
}

export default App;